import React from 'react';
import { Empty } from 'antd';
import styled from 'styled-components';

const PlotContainer = styled.div`
  padding: 40px 20px;
`;

const PlotWrapper = styled.div`
  position: relative;
  height: 100px;
`;

const ScaleAxis = styled.div`
  position: absolute;
  top: 50%;
  left: 10%;
  right: 10%;
  height: 2px;
  background-color: #d9d9d9;
  transform: translateY(-50%);
`;

const RangeBar = styled.div`
  position: absolute;
  top: 50%;
  height: 4px;
  background-color: #8c8c8c;
  border-radius: 4px;
  transform: translateY(-50%);
  z-index: 1;
  /* Inject dynamic properties */
  left: ${props => `${props.$rangeStartLeft}%`};
  width: ${props => `${props.$rangeWidth}%`};

  /* Vertical bar for the START (Low) side */
  &::before {
    content: '';
    position: absolute;
    left: -1px;
    top: 50%;
    height: 14px;
    width: 3px;
    background-color: #8c8c8c;
    border-radius: 1px;
    transform: translate(0, -50%);
  }

  /* Vertical bar for the END (High) side */
  &::after {
    content: '';
    position: absolute;
    right: -1px;
    top: 50%;
    height: 14px;
    width: 3px;
    background-color: #8c8c8c;
    border-radius: 1px;
    transform: translate(0, -50%);
  }
`;

const MarkerWrapper = styled.div`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: ${props => props.$zIndex || 2};
  /* Inject dynamic property */
  left: ${props => `${props.$position}%`};
`;

const MeanDiamond = styled.div`
  width: 12px;
  height: 12px;
  background-color: #8053d7;
  transform: rotate(45deg);
`;

const StudentCircle = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: #1890ff;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
`;

const TargetStudentCircle = styled(StudentCircle)`
  background-color: #f5222d;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
`;

const LabelsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
  padding-left: 10%;
  padding-right: 10%;
`;

const LabelGroup = styled.div`
  text-align: ${props => props.$align || 'left'};
`;

const LowHighLabel = styled.div`
  font-size: 12px;
  color: #8c8c8c;
`;

const MeanLabel = styled.div`
  font-size: 12px;
  color: #8053d7;
  font-weight: 700;
`;

const StudentValuesContainer = styled.div`
  margin-top: 20px;
  text-align: center;
`;

const StudentValue = styled.div`
  font-size: 14px;
  color: #1890ff;
  font-weight: 500;
`;

const TargetStudentValue = styled.div`
  font-size: 14px;
  color: #f5222d;
  font-weight: 500;
`;

export const ComparisonDotPlot = ({ comparisonData, studentName }) => {
  if (!comparisonData || comparisonData.length === 0) {
    return <Empty description='No comparison data available' />;
  }

  const low = comparisonData.find(d => d.type === 'Low')?.value || 0;
  const high = comparisonData.find(d => d.type === 'High')?.value || 0;
  const mean = comparisonData.find(d => d.type === 'Mean')?.value || 0;
  const students = comparisonData.filter(d => d.category === 'student');

  const scaleLow = 0;
  const scaleHigh = 100;

  const getPosition = (value) => {
    if (scaleHigh === scaleLow) return 50;
    return ((value - scaleLow) / (scaleHigh - scaleLow)) * 100;
  };

  const plotAreaStart = 10;
  const plotScaleFactor = 0.8;

  const lowPosition = getPosition(low) * plotScaleFactor;
  const highPosition = getPosition(high) * plotScaleFactor;
  const meanPosition = getPosition(mean) * plotScaleFactor;

  const rangeStartLeft = plotAreaStart + Math.min(lowPosition, highPosition);
  // Ensure a minimum width for visibility if low === high
  const rangeWidth = Math.abs(highPosition - lowPosition) || 0.5;

  return (
    <PlotContainer>
      <PlotWrapper>
        <ScaleAxis />

        <RangeBar
          $rangeStartLeft={rangeStartLeft}
          $rangeWidth={rangeWidth}
        />

        <MarkerWrapper $position={plotAreaStart + meanPosition} $zIndex={2}>
          <MeanDiamond />
        </MarkerWrapper>

        {students.map((student, index) => (
          <MarkerWrapper
            key={index}
            $position={plotAreaStart + getPosition(student.value) * plotScaleFactor}
            $zIndex={3}
          >
            {student.type === 'Target Student' ? <TargetStudentCircle /> : <StudentCircle />}
          </MarkerWrapper>
        ))}
      </PlotWrapper>

      <LabelsContainer>
        {/* Using an array of objects to map labels for stable keys */}
        {[
          { key: 'low', align: 'left', value: low, Label: LowHighLabel },
          { key: 'mean', align: 'center', value: mean, Label: MeanLabel, isMean: true },
          { key: 'high', align: 'right', value: high, Label: LowHighLabel }
        ].map(({ key, align, value, Label, isMean }) => (
          <LabelGroup key={key} $align={align}>
            <Label>{isMean ? 'Mean' : key.charAt(0).toUpperCase() + key.slice(1)}: {value.toFixed(1)}</Label>
          </LabelGroup>
        ))}
      </LabelsContainer>

      <StudentValuesContainer>
        {students.map((student, index) => {
          return student.type === 'Target Student'
            ? <TargetStudentValue key={index}>
              {student.type}: {student.value.toFixed(1)}%
            </TargetStudentValue>
            : <StudentValue key={index}>
              {student.type}: {student.value.toFixed(1)}%
            </StudentValue>;
        })}
      </StudentValuesContainer>
    </PlotContainer>
  );
};
